import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { APIRoutes } from '../global-misc/api-routes';
import { firstValueFrom } from 'rxjs';
import {
  CreatePatientFormAssignmentModel,
  UpdatePatientFormAssignmentModel,
  PatientFormAssignmentModel,
  PatientFormAssignmentPageModel,
} from '../global-data-models/form-assignment.model';

export enum OrderDir {
  ASC = 'asc',
  DESC = 'desc',
}

export interface PatientFormAssignmentPageOptions {
  orgId: string;
  patientId: string;
  page: number;
  orderDir: OrderDir;
  limit: number;
  search?: string;
  orderBy?: string;
  status?: string;
}

export interface FormAssignmentPageOptions {
  orgId: string;
  page: number;
  orderDir: OrderDir;
  limit: number;
  search?: string;
  orderBy?: string;
  status?: string;
}

export const DEFAULT_PATIENT_FORM_ASSIGNMENT_PAGE_OPTIONS: PatientFormAssignmentPageOptions = {
  orgId: 'NO_ORG_ID',
  patientId: 'NO_PATIENT_ID',
  page: 1,
  orderDir: OrderDir.DESC,
  limit: 30,
  orderBy: 'assignedAt',
};

export const DEFAULT_FORM_ASSIGNMENT_PAGE_OPTIONS: FormAssignmentPageOptions = {
  orgId: 'NO_ORG_ID',
  page: 1,
  orderDir: OrderDir.ASC,
  limit: 30,
  orderBy: 'assignedAt',
};

@Injectable({ providedIn: 'root' })
export class FormAssignmentService {
  constructor(private http: HttpClient) { }

  async getPatientFormAssignment(orgId: string, patientId: string, assignmentId: string): Promise<PatientFormAssignmentModel> {
    const apiRoute = APIRoutes.SINGLE_PATIENT_FORM_ASSIGNMENTS_ROUTE.replace(':orgId', orgId).replace(':patientId', patientId).replace(':id', assignmentId);
    return await firstValueFrom(this.http.get<PatientFormAssignmentModel>(apiRoute));
  }

  async getPatientFormAssignmentsPage(options: PatientFormAssignmentPageOptions): Promise<PatientFormAssignmentPageModel> {
    const apiRoute = new URL(APIRoutes.PATIENT_FORM_ASSIGNMENTS_ROUTE.replace(':orgId', options.orgId).replace(':patientId', options.patientId));

    // Add query params
    apiRoute.searchParams.append('page', String(options.page));
    apiRoute.searchParams.append('orderDir', String(options.orderDir));
    apiRoute.searchParams.append('limit', String(options.limit));
    if (options.search) apiRoute.searchParams.append('search', String(options.search));
    if (options.orderBy) apiRoute.searchParams.append('orderBy', String(options.orderBy));
    if (options.status) apiRoute.searchParams.append('status', String(options.status));

    const response = await firstValueFrom(
      this.http.get<{
        items: any[];
        total: number;
        page: number;
        limit: number;
      }>(apiRoute.toString()),
    );

    // Map the backend response to match frontend expectations
    const mappedItems = response.items.map((item) => ({
      ...item,
      form: item.form
        ? {
          id: item.form.id,
          identifier: item.form.id, // Backend doesn't seem to have identifier, using id
          name: item.form.name,
          version: item.form.version,
        }
        : undefined,
    })); return {
      data: mappedItems,
      total: response.total,
      page: response.page,
      limit: response.limit,
    };
  }

  async getFormAssignmentsPage(options: FormAssignmentPageOptions): Promise<PatientFormAssignmentPageModel> {
    const apiRoute = new URL(APIRoutes.FORM_ASSIGNMENTS_ROUTE.replace(':orgId', options.orgId));

    // Add query params
    apiRoute.searchParams.append('page', String(options.page));
    apiRoute.searchParams.append('orderDir', String(options.orderDir));
    apiRoute.searchParams.append('limit', String(options.limit));
    if (options.search) apiRoute.searchParams.append('search', String(options.search));
    if (options.orderBy) apiRoute.searchParams.append('orderBy', String(options.orderBy));
    if (options.status) apiRoute.searchParams.append('status', String(options.status));

    console.log(apiRoute);

    const response = await firstValueFrom(
      this.http.get<{
        items: any[];
        total: number;
        page: number;
        limit: number;
      }>(apiRoute.toString()),
    );

    // Map the backend response to match frontend expectations
    const mappedItems = response.items.map((item) => ({
      ...item,
      form: item.form
        ? {
          id: item.form.id,
          identifier: item.form.id, // Backend doesn't seem to have identifier, using id
          name: item.form.name,
          version: item.form.version,
        }
        : undefined,
    }));

    return {
      data: mappedItems,
      total: response.total,
      page: response.page,
      limit: response.limit,
    };
  }

  async createPatientFormAssignment(orgId: string, patientId: string, assignment: CreatePatientFormAssignmentModel): Promise<PatientFormAssignmentModel> {
    const apiRoute = APIRoutes.PATIENT_FORM_ASSIGNMENTS_ROUTE.replace(':orgId', orgId).replace(':patientId', patientId);
    return await firstValueFrom(this.http.post<PatientFormAssignmentModel>(apiRoute, assignment));
  }

  async updatePatientFormAssignment(orgId: string, patientId: string, assignmentId: string, assignment: UpdatePatientFormAssignmentModel): Promise<PatientFormAssignmentModel> {
    const apiRoute = APIRoutes.SINGLE_PATIENT_FORM_ASSIGNMENTS_ROUTE.replace(':orgId', orgId).replace(':patientId', patientId).replace(':id', assignmentId);
    return await firstValueFrom(this.http.patch<PatientFormAssignmentModel>(apiRoute, assignment));
  }

  async deletePatientFormAssignment(orgId: string, patientId: string, assignmentId: string): Promise<void> {
    const apiRoute = APIRoutes.SINGLE_PATIENT_FORM_ASSIGNMENTS_ROUTE.replace(':orgId', orgId).replace(':patientId', patientId).replace(':id', assignmentId);
    return await firstValueFrom(this.http.delete<void>(apiRoute));
  }

  async deletePatientFormAssignments(orgId: string, patientId: string): Promise<void> {
    const apiRoute = APIRoutes.PATIENT_FORM_ASSIGNMENTS_ROUTE.replace(':orgId', orgId).replace(':patientId', patientId);
    return await firstValueFrom(this.http.delete<void>(apiRoute));
  }
}
